import type { PropsWithChildren } from "react"
import React from "react"
import type { ProviderTheme } from "@trycourier/react-provider"
import { CourierProvider } from "@trycourier/react-provider"
import { useColors, useIsDarkMode } from "@mallardbay/lib-react-components"

import { useGetCourierInboxAuthTokenQuery } from "~graphql/courier-inbox.generated"
import { useAuth } from "~utils/hooks/use-auth"

export default function CourierInboxProvider({ children }: PropsWithChildren) {
    const { isAuthenticated } = useAuth()
    const courierTheme = useCourierTheme()

    // don't query if user is not signed in
    const { data } = useGetCourierInboxAuthTokenQuery({
        skip: !isAuthenticated,
    })

    return (
        <CourierProvider
            // provide a key here to force a re-render when the token changes
            // this fixes unread messages getting queried before the token is available
            key={data?.getCourierInboxAuthToken}
            authorization={data?.getCourierInboxAuthToken}
            theme={courierTheme}
        >
            {children}
        </CourierProvider>
    )
}

// the provider (not the Inbox component) resolves colorMode into the
// css variables that drive the inbox popup colors
function useCourierTheme(): ProviderTheme {
    const colors = useColors()
    const isDarkMode = useIsDarkMode()

    if (!isDarkMode) {
        return { colorMode: "light" }
    }

    return {
        colorMode: "dark",
        // courier's dark background doesn't match our theme, so pin it
        variables: {
            background: colors.bgSecondary,
        },
    }
}
